/* istanbul ignore file */
import { ContentStatus } from 'src/app/shared/enums/content-status';
import { ITag } from '../models/i-tag';
import { ITopic } from '../models/i-topic';
import { IContentSetContentDto } from './interfaces/i-content-set-content-dto';
import { IImgMediaDto } from './interfaces/i-img-media-dto';
import { IVideoMediaDto } from './interfaces/i-video-media-dto';
import { IAuthor } from '../models/i-author';
import { IContentListDto } from './interfaces/i-content-list-dto';
import { IDynamicData } from './interfaces/i-dynamic-data';
import { ISurtitle } from '../models/i-surtitle';
import { IChapter } from '../models/i-chapter';

export class ContentSetContentDto implements IContentSetContentDto {
  public id!: string;
  public contentId!: string;
  public cas!: string;
  public landingPageHeadline!: string;
  public headline!: string;
  public body!: string;
  public mainImage: IImgMediaDto;
  public displayImageOnWebsite: boolean;
  public bodyImages!: IImgMediaDto[];
  public bodyVideos!: IVideoMediaDto[];
  public languageCode!: string;
  public deliveryTargetCodes!: string[];
  public tags!: ITag[];
  public topics!: ITopic[];
  public surtitles!: string[];
  public surtitlesDetailed: ISurtitle[];
  public status!: ContentStatus;
  public lastUpdatedDate!: Date;
  public lastUpdatedByUser!: string;
  public createdDate!: Date;
  public publishedDate!: Date;
  public presetPublishDate!: Date;
  public timezoneIdentifier!: string;
  public authors!: IAuthor[];
  public contentLists!: IContentListDto[];
  public isSpiked!: boolean;
  public restrictToRegisteredUsers!: boolean;
  public hideThisArticle!: boolean;
  public notForGoogleNewsSitemaps!: boolean;
  public notForNewsFeeds!: boolean;
  public paywallPremiumContent: boolean;
  public notForCompetitors: boolean;
  public dynamicData!: IDynamicData;
  public contentSetHeadline!: string;
  public strapline!: string;
  public bannerImage!: IImgMediaDto;
  public sponsorLogos!: IImgMediaDto[];
  public primaryColourHex!: string;
  public secondaryColourHex!: string;
  public highlightColourHex!: string;
  public chapters!: IChapter[];
  public isPublished!: () => boolean;
  public isCurrentLiveVersion!: () => boolean;

  constructor({
    id,
    contentId,
    cas,
    landingPageHeadline,
    headline,
    mainImage,
    displayImageOnWebsite,
    body,
    bodyImages,
    bodyVideos,
    languageCode,
    deliveryTargetCodes,
    tags,
    topics,
    surtitles,
    surtitlesDetailed,
    status,
    lastUpdatedDate,
    lastUpdatedByUser,
    createdDate,
    publishedDate,
    presetPublishDate,
    timezoneIdentifier,
    authors,
    contentLists,
    isSpiked,
    restrictToRegisteredUsers,
    hideThisArticle,
    notForGoogleNewsSitemaps,
    notForNewsFeeds,
    paywallPremiumContent,
    notForCompetitors,
    dynamicData,
    contentSetHeadline,
    strapline,
    bannerImage,
    sponsorLogos,
    primaryColourHex,
    secondaryColourHex,
    highlightColourHex,
    chapters,
  }: IContentSetContentDto) {
    this.id = id;
    this.contentId = contentId;
    this.cas = cas;
    this.landingPageHeadline = landingPageHeadline;
    this.headline = headline;
    this.mainImage = mainImage;
    this.displayImageOnWebsite = displayImageOnWebsite;
    this.body = body;
    this.bodyImages = bodyImages ?? [];
    this.bodyVideos = bodyVideos ?? [];
    this.languageCode = languageCode;
    this.deliveryTargetCodes = deliveryTargetCodes ?? [];
    this.tags = tags ?? [];
    this.topics = topics ?? [];
    this.surtitles = surtitles ?? [];
    this.surtitlesDetailed = surtitlesDetailed ?? [];
    this.status = status;
    this.lastUpdatedDate = lastUpdatedDate;
    this.lastUpdatedByUser = lastUpdatedByUser;
    this.createdDate = createdDate;
    this.publishedDate = publishedDate;
    this.presetPublishDate = presetPublishDate;
    this.timezoneIdentifier = timezoneIdentifier;
    this.authors = authors ?? [];
    this.contentLists = contentLists ?? [];
    this.isSpiked = isSpiked;
    this.restrictToRegisteredUsers = restrictToRegisteredUsers;
    this.hideThisArticle = hideThisArticle;
    this.notForGoogleNewsSitemaps = notForGoogleNewsSitemaps;
    this.notForNewsFeeds = notForNewsFeeds;
    this.paywallPremiumContent = paywallPremiumContent;
    this.notForCompetitors = notForCompetitors;
    this.dynamicData = { ...dynamicData };
    this.contentSetHeadline = contentSetHeadline;
    this.strapline = strapline;
    this.bannerImage = bannerImage;
    this.sponsorLogos = sponsorLogos ?? [];
    this.primaryColourHex = primaryColourHex;
    this.secondaryColourHex = secondaryColourHex;
    this.highlightColourHex = highlightColourHex;
    this.chapters = chapters ?? [];
    this.isPublished = () => this.status === ContentStatus.PUBLISHED;
    this.isCurrentLiveVersion = () =>
      this.id === this.dynamicData?.latestPublishedVersionId;
  }
}
